import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getPlayerAvatar } from '../../utils/player';
import ModalCloseButton from './ModalCloseButton';

const LeaveClubModal = ({
  club,
  user,
  allPlayers = [],
  onClose,
  onConfirmLeave,
}) => {
  const isOwner = Boolean(
    club?.ownerId === user?.uid ||
    club?.members?.[user?.uid]?.role === 'owner' ||
    (club?.members && Object.values(club.members).some(m => m.name === user?.displayName && m.role === 'owner'))
  );

  const extractName = (val) => {
    if (typeof val === 'string') return val;
    if (val && typeof val === 'object') {
      if (typeof val.name === 'string') return val.name;
      if (typeof val.displayName === 'string') return val.displayName;
    }
    return '';
  };

  const extractPhoto = (val) => {
    if (val && typeof val === 'object') {
      return val.photoUrl || val.photoURL || null;
    }
    return null;
  };

  const getCandidates = () => {
    const list = [];
    const addedNames = new Set();
    const currentUserDisplayName = (user?.displayName || '').toLowerCase().trim();

    if (club?.members && typeof club.members === 'object') {
      Object.entries(club.members).forEach(([key, val]) => {
        const nameStr = extractName(val) || (typeof key === 'string' && !key.startsWith('uid') ? key : '');
        const uidStr = typeof key === 'string' ? key : (val?.uid || nameStr);

        if (nameStr && uidStr && uidStr !== user?.uid && nameStr.toLowerCase().trim() !== currentUserDisplayName) {
          if (!addedNames.has(nameStr.toLowerCase())) {
            list.push({
              uid: String(uidStr),
              name: String(nameStr),
              photoUrl: extractPhoto(val),
            });
            addedNames.add(nameStr.toLowerCase());
          }
        }
      });
    }

    if (Array.isArray(club?.players)) {
      club.players.forEach((item) => {
        const nameStr = extractName(item);
        if (nameStr && nameStr.toLowerCase().trim() !== currentUserDisplayName && !addedNames.has(nameStr.toLowerCase())) {
          const match = (allPlayers || []).find((p) => p.name && p.name.toLowerCase().trim() === nameStr.toLowerCase());
          list.push({
            uid: String(match?.uid || nameStr),
            name: String(nameStr),
            photoUrl: match?.photoUrl || null,
          });
          addedNames.add(nameStr.toLowerCase());
        }
      });
    }

    return list;
  };

  const candidates = getCandidates();
  const requiresTransfer = isOwner && candidates.length > 0;

  // 1 = Transfer ownership, 2 = Are you sure?
  const [step, setStep] = useState(requiresTransfer ? 1 : 2);
  const [selectedNewOwnerUid, setSelectedNewOwnerUid] = useState('');

  useEffect(() => {
    if (candidates.length > 0) {
      setSelectedNewOwnerUid(candidates[0].uid);
    }
  }, [club]);

  const selectedCandidate = candidates.find(c => c.uid === selectedNewOwnerUid) || candidates[0];
  const selectedName = selectedCandidate ? String(selectedCandidate.name) : 'Select player';
  const selectedPhoto = selectedCandidate ? selectedCandidate.photoUrl : null;

  return createPortal(
    <div className="modal-overlay">
      <div className="modal">
        <ModalCloseButton onClick={onClose} />

        {/* STEP 1: TRANSFER OWNERSHIP (Owner only) */}
        {step === 1 ? (
          <div>
            <h3 className="modal-title">
              Transfer ownership
            </h3>

            <p className="modal-text" style={{ marginBottom: 24 }}>
              Before leaving <strong>{club?.name}</strong> you need to transfer ownership of the club to another player.
            </p>

            <div className="mb-6 text-center">
              <p className="body-medium font-bold text-white mb-2">{club?.name}</p>

              <div className="select-field select-field--rich">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedPhoto || getPlayerAvatar(allPlayers, selectedName)}
                    alt={selectedName}
                    className="w-9 h-9 rounded-full object-cover border border-white/20"
                  />
                  <span className="font-bold text-white">{selectedName}</span>
                </div>

                <span className="material-symbols-rounded select-field__icon" aria-hidden="true">
                  keyboard_arrow_down
                </span>

                <select
                  value={selectedNewOwnerUid}
                  onChange={(e) => setSelectedNewOwnerUid(e.target.value)}
                  className="select-field__overlay"
                  aria-label="New owner"
                >
                  {candidates.map((c) => (
                    <option key={String(c.uid)} value={String(c.uid)} className="bg-slate-900 text-white">
                      {String(c.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setStep(2)}
              >
                <span className="btn-text">NEXT</span>
              </button>
            </div>
          </div>
        ) : (
          /* STEP 2: CONFIRM LEAVE */
          <div>
            <h3 className="modal-title">
              Leave Club
            </h3>

            <div className="modal-body">
              <p className="modal-text">
                Are you sure you want to leave <strong>{club?.name}</strong>? You will be removed from the club roster.
              </p>
              <p className="modal-text modal-text-strong">
                Once confirmed this can't be undone.
              </p>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  onConfirmLeave(club.id, selectedNewOwnerUid);
                  onClose();
                }}
              >
                <span className="btn-text">CONFIRM LEAVE</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LeaveClubModal;
